import { useMemo } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { DateTime } from "luxon"
import { CellContext } from "@tanstack/react-table"
import Table from "../../../../table"
import { AdvisorThread, BBColumnDef } from "../../../../../../types"

const formatDate = (iso: string) =>
  DateTime.fromISO(iso).toFormat("MMM dd, yyyy · h:mm a")

const getLatestUserMessage = (thread: AdvisorThread): string =>
  [...thread.messages].reverse().find(m => m.role === "user")?.content ?? ""

interface ConversationsTableProps {
  threads: AdvisorThread[]
  leadName: string
  sessionId: string
}

const ConversationsTable = ({
  threads,
  leadName,
  sessionId
}: ConversationsTableProps) => {
  const totalThreads = threads.length

  const columns = useMemo<BBColumnDef<AdvisorThread>[]>(
    () => [
      {
        id: "leadName",
        header: "Lead Name",
        meta: { headerClassName: "py-4 pl-2 pr-3 text-left w-1/5" },
        cell: ({ row }: CellContext<AdvisorThread, unknown>) => (
          <td className="py-4 pl-2 pr-3 truncate">
            <div className="font-semibold">{leadName}</div>
            <time
              dateTime={row.original.lastActivityAt}
              className="text-xs text-neutral-400 block"
            >
              {formatDate(row.original.lastActivityAt)}
            </time>
          </td>
        )
      },
      // TODO: re-enable insights column
      // {
      //   id: "insights",
      //   header: "Insights",
      //   meta: { headerClassName: "w-36 py-4 text-left" },
      //   cell: ({ row }: CellContext<AdvisorThread, unknown>) => (
      //     <td className="whitespace-nowrap py-4 pl-2 pr-3">
      //       <button
      //         type="button"
      //         className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded border border-indigo-100 hover:bg-indigo-100 transition-colors"
      //         onClick={() => console.log("View insights", row.original._id)}
      //       >
      //         View insights
      //       </button>
      //     </td>
      //   )
      // },
      {
        id: "snippet",
        header: "Latest Message",
        meta: { headerClassName: "py-4 text-left" },
        cell: ({ row }: CellContext<AdvisorThread, unknown>) => {
          const snippet = getLatestUserMessage(row.original)
          return (
            <td className="whitespace-nowrap py-4 cursor-pointer truncate max-w-0 text-sm">
              {snippet ? (
                `"${snippet}"`
              ) : (
                <span className="text-neutral-300">—</span>
              )}
            </td>
          )
        }
      },
      {
        id: "action",
        header: "Action",
        meta: { headerClassName: "w-16 sr-only" },
        cell: () => (
          <td className="px-5 py-3 text-right">
            <Link
              target="_blank"
              to={`/conversations/advisor/${sessionId}`}
              className="inline-flex text-neutral-400 hover:text-purple-400 transition-colors"
              title="Open in Conversations"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </td>
        )
      }
    ],
    [leadName, sessionId]
  )

  return (
    <>
      <div className="py-4 border-b border-neutral-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold">AI-Driven Conversations</h3>
        <span className="text-xs">
          Total Conversations:{" "}
          <span className="font-semibold text-neutral-700">{totalThreads}</span>
        </span>
      </div>

      {totalThreads === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-400">
          No conversation threads found.
        </div>
      ) : (
        <Table
          data={threads}
          viewType="table"
          searchBarVisible={false}
          noSorter
          columns={columns}
          tableContainerClassName="w-full overflow-auto"
        />
      )}
    </>
  )
}

export default ConversationsTable
